/**
 * 首页模块注册表 —— **唯一事实源**（2026-10-08）。
 *
 * 🔴 为什么必须集中在这里：
 * 改版前的导航是「GlobalHeader 里写一份 href 数组 + 各页面自己写一份面包屑」，
 * 同一个「工作台」在三处的路径分别是 `/studio`、`/zh/studio`、`/plugins/…/zh/studio`，
 * 改 basePath 时漏一处就是一个死链，而且**不报错**（只是点不动）。
 *
 * 现在：路径只有这一个数组里有。GlobalHeader 首页入口、快捷入口宫格、
 * 常用功能列表、运行状态定位全部读它 —— 加一个模块只改一处。
 *
 * ## 为什么 `path` 是模板而不是成品 URL
 *
 * 本项目跑在 basePath 子路径下（`/plugins/dsh-image-gen/aicomic`），
 * 且支持 4 种语言。成品 URL 需要 `${basePath}` 与 `${locale}` 两个变量，
 * 在纯数据模块里拼出来就得引入 env 与运行时判断（不可测、且 i18n 切换要重算）。
 * 所以这里只声明**域内路径**，由渲染方（`resolveModuleHref`）替换 ——
 * 替换规则集中在一个函数里，可单测。
 */

import type { HomeModule, HomeModuleGroup, HomeModuleGroupMeta } from "./types";

export const HOME_MODULE_GROUPS: HomeModuleGroupMeta[] = [
  { id: "create", labelKey: "home.groups.create" },
  { id: "manage", labelKey: "home.groups.manage" },
  { id: "config", labelKey: "home.groups.config" },
];

export const HOME_MODULES: HomeModule[] = [
  {
    id: "projects",
    labelKey: "home.modules.projects.label",
    descKey: "home.modules.projects.desc",
    path: "/{locale}",
    icon: "FolderOpen",
    group: "manage",
    probe: "db",
  },
  {
    id: "studio",
    labelKey: "home.modules.studio.label",
    descKey: "home.modules.studio.desc",
    path: "/{locale}/studio",
    icon: "Palette",
    group: "create",
  },
  // 🔴 「画库」独立入口已移除（2026-10-08 用户截图圈删）：
  //    它与 工作台 → 画库 标签页完全同页，首页再给一个捷径属于同一功能两个入口。
  //    画库功能本身保留（studio-tabs 的 tabGallery），只是不再从首页直达。
  //    旧 localStorage 里遗留的 gallery 置顶 id 由 useSortedModules 的
  //    findModule().filter(Boolean) 静默丢弃，无需迁移。
  // 🔴 「灵感」入口已移除（2026-10-09 用户点名「常用功能 移除 灵感 这个入口」）：
  //    灵感页**功能本身保留**（studio → 灵感 tab 与 /studio/inspiration 路由、
  //    代理 API 都在），只是不再从首页/常用功能直达 —— 与上方「画库」同一处置。
  //    旧 localStorage 里遗留的 inspiration 置顶/隐藏 id 由 useSortedModules 的
  //    findModule().filter(Boolean) 静默丢弃，无需迁移（同 gallery 先例）。
  {
    id: "pipeline",
    labelKey: "home.modules.pipeline.label",
    descKey: "home.modules.pipeline.desc",
    path: "/{locale}/pipeline",
    icon: "Workflow",
    group: "create",
    probe: "pipeline",
  },
  {
    // agent 控制台：飞书端收到的指令 / 发布的任务 / 执行过程都在这里。
    // 挂在 manage 组（它是「照看生产」的面板，不是产内容的工作台）。
    id: "agent",
    labelKey: "home.modules.agent.label",
    descKey: "home.modules.agent.desc",
    path: "/{locale}/agent",
    icon: "Bot",
    group: "manage",
  },
  {
    id: "assets",
    labelKey: "home.modules.assets.label",
    descKey: "home.modules.assets.desc",
    path: "/{locale}/assets",
    icon: "Images",
    group: "manage",
  },
  {
    id: "skills",
    labelKey: "home.modules.skills.label",
    descKey: "home.modules.skills.desc",
    path: "/{locale}/skills",
    icon: "BookOpen",
    group: "config",
  },
  {
    id: "prompts",
    labelKey: "home.modules.prompts.label",
    descKey: "home.modules.prompts.desc",
    path: "/{locale}/settings/prompts",
    icon: "Wand2",
    group: "config",
  },
  {
    id: "settings",
    labelKey: "home.modules.settings.label",
    descKey: "home.modules.settings.desc",
    path: "/{locale}/settings",
    icon: "Settings",
    group: "config",
  },
];

/**
 * 把注册表里的域内路径模板解析成可跳转的 href。
 *
 * 🔴 抽成独立纯函数的原因：`/zh/studio` 与 `/plugins/dsh-image-gen/aicomic/zh/studio`
 * 的差别只有前缀，而**前缀算错的表现是「点了没反应」而不是报错**。
 * 放进纯函数后可以对 9 个模块 × 4 语言做全组合断言（门禁 `home-modules-gate.test.mjs`），
 * 而不用真的点 36 次。
 *
 * @param basePath 子路径挂载前缀，末尾不带 `/`；空串表示挂在 origin 根
 */
export function resolveModuleHref(
  module: Pick<HomeModule, "path">,
  locale: string,
  basePath = "",
): string {
  const prefix = basePath.replace(/\/+$/, "");
  const path = module.path.replace("{locale}", locale);
  return `${prefix}${path}`;
}

/** 按 id 取模块；未注册返回 undefined（**不抛** —— 偏好数据可能来自旧版本 localStorage）。 */
export function findModule(id: string): HomeModule | undefined {
  return HOME_MODULES.find((m) => m.id === id);
}

/** 按分组顺序返回模块（快捷入口宫格与常用功能共用同一遍历顺序）。 */
export function modulesByGroup(): { group: HomeModuleGroupMeta; modules: HomeModule[] }[] {
  return HOME_MODULE_GROUPS.map((g) => ({
    group: g,
    modules: HOME_MODULES.filter((m) => m.group === g.id),
  })).filter((g) => g.modules.length > 0);
}
